import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Loader2, RefreshCw, CheckCircle, XCircle, Trash2 } from 'lucide-react';

/**
 * Force Refresh Page - Clears all caches and restores session
 * Use this when the PWA has issues after reinstall
 * URL: /force-refresh
 */
const ForceRefresh = () => {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [status, setStatus] = useState('idle'); // idle, clearing, restoring, success, error
  const [steps, setSteps] = useState([]);
  const [error, setError] = useState(null);

  const addStep = (text, success = true) => {
    setSteps(prev => [...prev, { text, success, time: new Date().toLocaleTimeString() }]);
  };

  const clearAllCaches = async () => {
    setStatus('clearing');
    setSteps([]);
    setError(null);

    try {
      // Step 1: Unregister all service workers
      addStep('Désactivation des Service Workers...');
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
        }
        addStep(`${registrations.length} Service Worker(s) supprimé(s)`);
      } else {
        addStep('Pas de Service Workers');
      }

      // Step 2: Clear all caches
      addStep('Nettoyage du cache...');
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        for (const cacheName of cacheNames) {
          await caches.delete(cacheName);
        }
        addStep(`${cacheNames.length} cache(s) supprimé(s)`);
      } else {
        addStep('Pas de cache à nettoyer');
      }

      // Step 3: Clear localStorage (except session token)
      addStep('Nettoyage du stockage local...');
      const sessionToken = localStorage.getItem('session_token');
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key !== 'session_token') {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      addStep(`${keysToRemove.length} élément(s) nettoyé(s)`);

      // Step 4: Restore session
      setStatus('restoring');
      addStep('Restauration de la session...');

      if (sessionToken) {
        // Try to validate existing token
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
          headers: { 'X-Session-Token': sessionToken }
        });

        if (response.ok) {
          const userData = await response.json();
          updateUser(userData);
          addStep(`Session restaurée: ${userData.name || userData.email}`);
          setStatus('success');
        } else {
          // Token invalid, try direct login
          addStep('Token expiré, tentative de connexion directe...');
          await tryDirectLogin();
        }
      } else {
        // No token, try direct login
        addStep('Pas de token, tentative de connexion directe...');
        await tryDirectLogin();
      }

    } catch (err) {
      console.error('Force refresh error:', err);
      setError(err.message);
      addStep(`Erreur: ${err.message}`, false);
      setStatus('error');
    }
  };

  const tryDirectLogin = async () => {
    // No valid token exists — user must re-authenticate via Google
    throw new Error('Session expirée. Veuillez vous reconnecter via Google.');
  };

  const goToDashboard = () => {
    navigate('/dashboard', { replace: true });
  };

  const goToLogin = () => {
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="w-16 h-16 mx-auto bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mb-4">
            <RefreshCw className={`w-10 h-10 text-white ${status === 'clearing' || status === 'restoring' ? 'animate-spin' : ''}`} />
          </div>
          <h1 className="text-2xl font-bold">Force Refresh</h1>
          <p className="text-muted-foreground mt-2">
            Nettoie le cache et restaure votre session
          </p>
        </div>

        {/* Status Steps */}
        {steps.length > 0 && (
          <div className="bg-card border border-border rounded-lg p-4 space-y-2">
            {steps.map((step, index) => (
              <div key={index} className="flex items-center gap-2 text-sm">
                {step.success ? (
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                )}
                <span className={step.success ? 'text-foreground' : 'text-red-500'}>
                  {step.text}
                </span>
              </div>
            ))}
            
            {(status === 'clearing' || status === 'restoring') && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{status === 'clearing' ? 'Nettoyage en cours...' : 'Restauration...'}</span>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          {status === 'idle' && (
            <Button
              onClick={clearAllCaches}
              className="w-full bg-orange-500 hover:bg-orange-600"
              size="lg"
            >
              <Trash2 className="w-5 h-5 mr-2" />
              Nettoyer et Restaurer
            </Button>
          )}

          {status === 'success' && (
            <>
              <div className="text-center text-green-500 font-medium py-2">
                ✓ Session restaurée avec succès!
              </div>
              <Button
                onClick={goToDashboard}
                className="w-full bg-green-500 hover:bg-green-600"
                size="lg"
              >
                Aller au Dashboard
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="text-center text-red-500 font-medium py-2">
                Erreur: {error}
              </div>
              <Button
                onClick={goToLogin}
                className="w-full bg-orange-500 hover:bg-orange-600"
                size="lg"
              >
                Se reconnecter via Google
              </Button>
              <Button
                onClick={clearAllCaches}
                variant="outline"
                className="w-full"
                size="lg"
              >
                <RefreshCw className="w-5 h-5 mr-2" />
                Réessayer
              </Button>
            </>
          )}
        </div>

        {/* Info */}
        <p className="text-xs text-center text-muted-foreground">
          Cette page nettoie les données corrompues de l'application.<br/>
          Utilisez-la si l'app ne fonctionne pas après une réinstallation.
        </p>
      </div>
    </div>
  );
};

export default ForceRefresh;
